import {
  AlertDescription,
  AlertTitle,
  Box,
  Button,
  chakra,
  Collapse,
  Flex,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HighlightDaysAndTimes } from '~components/powerSchedules/PowerScheduleDetail/HighlightDaysAndTimes';
import { TimeSelect } from '~components/powerSchedules/TimeSelect';
import { ErrorAlert, WarningAlert } from '~components/ui/Alert';
import { ChevronDownIcon, InfoIcon } from '~components/ui/icons';
import { BrightnessIcon } from '~components/ui/icons/BrightnessIcon';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import { Slider } from '~components/ui/Slider';
import { useTheme } from '~components/ui/styles/hooks';
import {
  Day,
  ScheduleTimeBlockInput,
  TimeBlockCategory,
  TimeBlockType,
} from '~graphql/__generated__/types';
import { useContentBoxWidth } from '~utils/dimensions';
import {
  assignTimeBlockPriorities,
  DEFAULT_INPUT_SOURCE_VALUE,
  endAfterStart,
  getTimeBlockCategory,
  hasCompleteTimeBlockData,
  hasCompleteTimeBlockTimes,
  MAX_PRIORITY_LANES,
  MIN_SLOT_DURATION_MINUTES,
  ScheduleValidationError,
  timeBlockTypesForCategory,
  toMinutes,
  validateAndSortScheduleTimeBlocks,
} from '../scheduleTimeBlocks';
import { TIME_BLOCK_CONTENT_COLOR, TIME_BLOCK_DATA_LABEL, TIME_BLOCK_META } from '../timeBlockMeta';
import {
  AppOption,
  DEFAULT_BRIGHTNESS,
  IconSelect,
  PlaylistOption,
} from '../useEditScheduleTimeBlocks';
import {
  BLOCK_GAP,
  BLOCK_ICON_SIZE,
  BLOCK_PADDING_LEFT,
  BLOCK_PADDING_RIGHT,
  ICON_ONLY_MIN_WIDTH,
  WITH_TIME_MIN_WIDTH,
} from './ScheduleTimeBlockBox';

const DAY_MINUTES = 24 * 60;
/** Dragging lands on quarter hours, the same granularity as the shortest slot. */
const SNAP_MINUTES = 15;

/**
 * Lane metrics from the design: 40px slots on a 44px pitch inside a 4px inset, so
 * the five lanes it draws come to 224px.
 */
const LANE_HEIGHT = 40;
const LANE_PITCH = 64;
const LANE_BOX_HEIGHT = 48;
const LANE_TRACK_PADDING = 4;
const LANE_BOX_OFFSET = (LANE_PITCH - LANE_BOX_HEIGHT) / 2;
const CANVAS_INSET = LANE_BOX_OFFSET + LANE_TRACK_PADDING;
const PRIORITY_GUTTER_WIDTH = 50;
const PRIORITY_LANE_HINTS = [
  'schedulePriorityLane1',
  'schedulePriorityLane2',
  'schedulePriorityLane3',
  'schedulePriorityLane4',
  'schedulePriorityLane5',
];

/** Grab zone at each end of the selected slot, wide enough to hit comfortably. */
const RESIZE_HANDLE_WIDTH = 10;

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const snap = (minutes: number) => Math.round(minutes / SNAP_MINUTES) * SNAP_MINUTES;

const formatMinutes = (minutes: number) => {
  const wrapped = minutes % DAY_MINUTES;
  const hours = Math.floor(wrapped / 60);
  const rest = wrapped % 60;
  return `${String(hours).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
};

const displayTimeRange = (block: ScheduleTimeBlockInput) =>
  `${block.start} - ${block.end === '00:00' ? '24:00' : block.end}`;

/**
 * What a slot's payload starts as when its type changes. Most types start empty for the
 * user to fill in; the two that have no picker carry their value from the outset.
 */
const DATA_FOR_NEW_TYPE: Partial<Record<TimeBlockType, ScheduleTimeBlockInput['data']>> = {
  [TimeBlockType.Brightness]: { value: DEFAULT_BRIGHTNESS },
  [TimeBlockType.DefaultInputSource]: { source: DEFAULT_INPUT_SOURCE_VALUE },
};

/** A slot plus a key that survives the edits made while the dialog is open. */
type DraftSlot = { key: string; block: ScheduleTimeBlockInput };

type DragMode = 'move' | 'resize-start' | 'resize-end';

type DragState = {
  key: string;
  mode: DragMode;
  originX: number;
  originY: number;
  originStart: number;
  originEnd: number;
  originLane: number;
};

interface Props {
  day?: Day;
  category: TimeBlockCategory;
  /** Slot to open with already selected; omitted when opened from the stacked icon. */
  selectedTimeBlock?: ScheduleTimeBlockInput;
  timeBlocks: ScheduleTimeBlockInput[];
  playlists: PlaylistOption[];
  inputSources: string[];
  apps: AppOption[];
  isDisabled?: boolean;
  isOpen: boolean;
  onSubmit: (timeBlocks: ScheduleTimeBlockInput[]) => Promise<void> | void;
  onCancel: () => void;
}

export function EditScheduleTimeSlotsModal({ isOpen, onCancel, day, ...contentProps }: Props) {
  return (
    <Modal isOpen={isOpen} onClose={onCancel} scrollBehavior="inside" size="6xl" autoFocus={false}>
      <ModalOverlay />
      {/* Mounted per opening so the draft always starts from what is stored. */}
      {isOpen && day && (
        <EditScheduleTimeSlotsModalContent day={day} onCancel={onCancel} {...contentProps} />
      )}
    </Modal>
  );
}

type ContentProps = Omit<Props, 'isOpen' | 'day'> & { day: Day };

function EditScheduleTimeSlotsModalContent({
  day,
  category,
  selectedTimeBlock,
  timeBlocks,
  playlists,
  inputSources,
  apps,
  isDisabled,
  onSubmit,
  onCancel,
}: ContentProps) {
  const theme = useTheme();
  const canvasRef = useRef<HTMLDivElement>(null);
  const canvasWidth = useContentBoxWidth(canvasRef);

  const belongsToDay = useCallback(
    (block: ScheduleTimeBlockInput) =>
      block.day === day && getTimeBlockCategory(block.type) === category,
    [day, category],
  );

  /**
   * Lanes are re-derived on open. Stored blocks predate the five-lane stack - the
   * mutation still accepts priority 0, and older schedules were numbered from the top
   * down - so drawing them at their stored lane would put a slot outside the canvas
   * and make every drag invalid. Renumbering is idempotent for blocks already in the
   * current scheme, so a schedule that needs no repair is untouched. The result is
   * index-aligned with `timeBlocks`, which keeps the click-through selection valid.
   */
  const normalisedBlocks = useMemo(() => assignTimeBlockPriorities(timeBlocks), [timeBlocks]);

  const dayBlocks = useMemo(
    () => normalisedBlocks.filter(belongsToDay),
    [normalisedBlocks, belongsToDay],
  );

  // Slots on the other days and the other track ride along untouched, so the dialog
  // can hand back the schedule's whole block list on save.
  const untouchedBlocks = useMemo(
    () => normalisedBlocks.filter((block) => !belongsToDay(block)),
    [normalisedBlocks, belongsToDay],
  );

  const [draft, setDraft] = useState<DraftSlot[]>(() =>
    dayBlocks.map((block, index) => ({ key: `slot-${index}`, block })),
  );
  const [selectedKey, setSelectedKey] = useState<string | undefined>(() => {
    if (!selectedTimeBlock) return undefined;

    const index = timeBlocks.filter(belongsToDay).indexOf(selectedTimeBlock);
    return index === -1 ? undefined : `slot-${index}`;
  });
  const [drag, setDrag] = useState<DragState | undefined>();
  const [isSaving, setIsSaving] = useState(false);

  const selected = draft.find((slot) => slot.key === selectedKey);

  // The stack is a fixed five lanes deep, so the canvas always draws all of them.
  const laneCount = MAX_PRIORITY_LANES;

  /**
   * Same rules the mutation enforces: a lane may not hold two slots covering the
   * same minute, and a slot has to last between 15 minutes and a day.
   */
  const isValidDraft = useCallback(
    (slots: DraftSlot[]) => {
      try {
        const { conflictingTimeBlocks } = validateAndSortScheduleTimeBlocks(
          [...untouchedBlocks, ...slots.map((slot) => slot.block)],
          { throwError: false },
        );
        return conflictingTimeBlocks.length === 0;
      } catch {
        return false;
      }
    },
    [untouchedBlocks],
  );

  const replaceBlock = useCallback(
    (key: string, update: (block: ScheduleTimeBlockInput) => ScheduleTimeBlockInput) => {
      setDraft((current) => {
        const next = current.map((slot) =>
          slot.key === key ? { ...slot, block: update(slot.block) } : slot,
        );
        // A drag that would collide simply does not take, so the slot stops against
        // its neighbour instead of jumping into an invalid state.
        return isValidDraft(next) ? next : current;
      });
    },
    [isValidDraft],
  );

  /**
   * Edits made through the fields always take, even when the result conflicts: refusing
   * them leaves a slot that has been emptied with no way back, since every concrete time
   * the user then picks is judged against the day and thrown away. The conflict is
   * reported instead, and `draftError` holds the save back until it is resolved.
   */
  const replaceSelectedBlock = useCallback(
    (key: string, update: (block: ScheduleTimeBlockInput) => ScheduleTimeBlockInput) => {
      setDraft((current) =>
        current.map((slot) => (slot.key === key ? { ...slot, block: update(slot.block) } : slot)),
      );
    },
    [],
  );

  const beginDrag = (event: React.PointerEvent, slot: DraftSlot, mode: DragMode) => {
    if (isDisabled) return;
    event.preventDefault();
    event.stopPropagation();

    setSelectedKey(slot.key);
    setDrag({
      key: slot.key,
      mode,
      originX: event.clientX,
      originY: event.clientY,
      originStart: toMinutes(slot.block.start, 'start'),
      originEnd: toMinutes(slot.block.end, 'end'),
      originLane: slot.block.priority,
    });
  };

  useEffect(
    function trackPointerWhileDragging() {
      if (!drag) return;

      const onPointerMove = (event: PointerEvent) => {
        if (drag.mode === 'move') {
          const lanes = Math.round((event.clientY - drag.originY) / LANE_PITCH);
          const priority = clamp(drag.originLane + lanes, 1, MAX_PRIORITY_LANES);
          return replaceBlock(drag.key, (block) => ({ ...block, priority }));
        }

        if (!canvasWidth) return;

        const deltaMinutes = snap(((event.clientX - drag.originX) / canvasWidth) * DAY_MINUTES);

        if (drag.mode === 'resize-start') {
          const start = clamp(
            drag.originStart + deltaMinutes,
            0,
            drag.originEnd - MIN_SLOT_DURATION_MINUTES,
          );
          return replaceBlock(drag.key, (block) => ({ ...block, start: formatMinutes(start) }));
        }

        const end = clamp(
          drag.originEnd + deltaMinutes,
          drag.originStart + MIN_SLOT_DURATION_MINUTES,
          DAY_MINUTES - 1,
        );
        return replaceBlock(drag.key, (block) => ({ ...block, end: formatMinutes(end) }));
      };

      const onPointerUp = () => setDrag(undefined);

      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
      window.addEventListener('pointercancel', onPointerUp);

      return () => {
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', onPointerUp);
        window.removeEventListener('pointercancel', onPointerUp);
      };
    },
    [drag, canvasWidth, replaceBlock],
  );

  const updateSelectedTime = (property: 'start' | 'end', value: string) => {
    if (!selected) return;

    replaceSelectedBlock(selected.key, (block) => {
      const end = property === 'end' ? value : block.end;

      // Same rule as the single-slot form: a start moved past the end carries the end
      // with it rather than being rejected for crossing it.
      if (
        property === 'start' &&
        hasCompleteTimeBlockTimes({ ...block, start: value }) &&
        end !== '00:00' &&
        value >= end
      ) {
        return { ...block, start: value, end: endAfterStart(value) };
      }

      return { ...block, [property]: value };
    });
  };

  // Payloads do not carry across types, so switching type starts the new one empty.
  const updateSelectedType = (type: TimeBlockType) => {
    if (!selected) return;
    replaceSelectedBlock(selected.key, (block) => ({
      ...block,
      type,
      data: DATA_FOR_NEW_TYPE[type] ?? {},
    }));
  };

  const updateSelectedData = (data: ScheduleTimeBlockInput['data']) => {
    if (!selected) return;
    replaceSelectedBlock(selected.key, (block) => ({ ...block, data }));
  };

  const removeSelected = () => {
    if (!selected) return;
    setDraft((current) => current.filter((slot) => slot.key !== selected.key));
    setSelectedKey(undefined);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSubmit([...untouchedBlocks, ...draft.map((slot) => slot.block)]);
    } finally {
      setIsSaving(false);
    }
  };

  const hasIncompleteSlot = draft.some(
    (slot) => !hasCompleteTimeBlockData(slot.block) || !hasCompleteTimeBlockTimes(slot.block),
  );

  const draftError = useMemo(() => {
    try {
      validateAndSortScheduleTimeBlocks([...untouchedBlocks, ...draft.map((slot) => slot.block)], {
        useShortErrorMessage: true,
      });
    } catch (err) {
      return err instanceof ScheduleValidationError ? err : undefined;
    }
  }, [untouchedBlocks, draft]);

  const selectedMeta = selected ? TIME_BLOCK_META[selected.block.type] : undefined;
  const SelectedIcon = selectedMeta?.blockIcon;

  return (
    <ModalContent maxWidth="1323px">
      <ModalHeader display="flex" alignItems="center" gap="2">
        {t('editTimeSlot')}
        <Tooltip label={t('editTimeSlotsPriorityInfo')} placement="right" hasArrow>
          <chakra.span
            display="inline-flex"
            tabIndex={0}
            aria-label={t('editTimeSlotsPriorityInfo')}
          >
            <InfoIcon width="24px" height="24px" color="blue.500" />
          </chakra.span>
        </Tooltip>
      </ModalHeader>
      <ModalCloseButton onClick={onCancel} />
      <ModalBody>
        <Text marginBottom="4">{t('editTimeSlotsDesc')}</Text>

        <Flex alignItems="center" gap="4" marginBottom="6">
          <Menu placement="bottom-start">
            <MenuButton
              as={Button}
              variant="outline"
              height="40px"
              paddingX="2"
              isDisabled={!selected || isDisabled}
              background={selectedMeta?.badgeBackground}
            >
              <Flex alignItems="center" gap="1">
                {SelectedIcon ? (
                  <SelectedIcon width={6} height={6} color={selectedMeta?.blockIconColor} />
                ) : (
                  <chakra.span fontWeight="normal">{t('all')}</chakra.span>
                )}
                <ChevronDownIcon width={4} height={4} />
              </Flex>
            </MenuButton>
            <MenuList>
              {timeBlockTypesForCategory(category).map((type) => {
                const meta = TIME_BLOCK_META[type];
                const TypeIcon = meta.blockIcon;
                return (
                  <MenuItem key={type} onClick={() => updateSelectedType(type)}>
                    <Flex
                      alignItems="center"
                      justifyContent="center"
                      padding="1"
                      marginRight="3"
                      borderRadius="base"
                      background={meta.badgeBackground}
                    >
                      <TypeIcon width={6} height={6} color={meta.blockIconColor} />
                    </Flex>
                    {t(meta.label)}
                  </MenuItem>
                );
              })}
            </MenuList>
          </Menu>

          <Flex alignItems="center" gap="3">
            <Box width="82px">
              <TimeSelect
                allowIncompleteInput
                value={selected?.block.start ?? '00:00'}
                property="start"
                onChange={updateSelectedTime}
                disabled={!selected || isDisabled}
              />
            </Box>
            <chakra.span>-</chakra.span>
            <Box width="82px">
              <TimeSelect
                allowIncompleteInput
                value={selected?.block.end ?? '00:00'}
                property="end"
                endsBeforeMidnight
                onChange={updateSelectedTime}
                disabled={!selected || isDisabled}
              />
            </Box>
          </Flex>

          <Box flex="1" minWidth="0">
            {selected && (
              <SlotDataField
                block={selected.block}
                playlists={playlists}
                inputSources={inputSources}
                apps={apps}
                isDisabled={isDisabled}
                onChange={updateSelectedData}
              />
            )}
          </Box>

          {selected && (
            <Button
              variant="link"
              colorScheme="red"
              flexShrink={0}
              isDisabled={isDisabled}
              onClick={removeSelected}
            >
              {t('removeTimeSlot')}
            </Button>
          )}
        </Flex>

        <Collapse in={!!draftError} animateOpacity>
          <Box marginBottom={draftError ? 6 : 0} transition="margin 100ms">
            {draftError?.severity === 'warning' ? (
              <WarningAlert>{draftError.message}</WarningAlert>
            ) : (
              <ErrorAlert>
                <AlertTitle>{draftError?.title}</AlertTitle>
                <AlertDescription>
                  <HighlightDaysAndTimes>{draftError?.message ?? ''}</HighlightDaysAndTimes>
                </AlertDescription>
              </ErrorAlert>
            )}
          </Box>
        </Collapse>

        <Flex
          justifyContent="space-between"
          color="gray.800"
          marginBottom="2"
          paddingLeft={`${PRIORITY_GUTTER_WIDTH}px`}
        >
          <span>00:00</span>
          <span>06:00</span>
          <span>12:00</span>
          <span>18:00</span>
          <span>00:00</span>
        </Flex>

        <Flex>
          <Box position="relative" width={`${PRIORITY_GUTTER_WIDTH}px`} flexShrink={0}>
            <Box
              position="absolute"
              left="34px"
              top={`${LANE_PITCH / 2}px`}
              width="2px"
              height={`${(laneCount - 1) * LANE_PITCH}px`}
              background="gray.100"
            />
            {Array.from({ length: laneCount }, (_, lane) => (
              <Tooltip key={lane} label={t(PRIORITY_LANE_HINTS[lane])} placement="right" hasArrow>
                <Flex
                  position="relative"
                  height={`${LANE_PITCH}px`}
                  alignItems="center"
                  paddingX="2"
                  tabIndex={0}
                >
                  <Text fontFamily="heading" fontSize="xl" fontWeight="semibold" color="gray.800">
                    {lane + 1}
                  </Text>
                  <Box
                    position="absolute"
                    left="31px"
                    top="50%"
                    transform="translateY(-50%)"
                    boxSize="8px"
                    borderRadius="full"
                    background={lane === laneCount - 1 ? 'gray.400' : 'gray.800'}
                  />
                </Flex>
              </Tooltip>
            ))}
          </Box>
          <Box
            ref={canvasRef}
            flex="1"
            position="relative"
            marginX={`${LANE_TRACK_PADDING}px`}
            height={`${laneCount * LANE_PITCH}px`}
            onPointerDown={() => setSelectedKey(undefined)}
            sx={{ touchAction: 'none' }}
          >
            {Array.from({ length: laneCount }, (_, lane) => (
              <Box
                key={lane}
                position="absolute"
                top={`${lane * LANE_PITCH + LANE_BOX_OFFSET}px`}
                left={`-${LANE_TRACK_PADDING}px`}
                right={`-${LANE_TRACK_PADDING}px`}
                height={`${LANE_BOX_HEIGHT}px`}
                padding={`${LANE_TRACK_PADDING}px`}
                background="white"
                borderWidth={1}
                borderStyle="solid"
                borderColor="gray.100"
                borderRadius={4}
              >
                <Box height="100%" background="gray.50" borderRadius={4} />
              </Box>
            ))}
            {draft.map((slot) => {
              const { block } = slot;
              const meta = TIME_BLOCK_META[block.type];
              const BlockIcon = meta.blockIcon;
              const startMinutes = toMinutes(block.start, 'start');
              const endMinutes = toMinutes(block.end, 'end');
              const widthRatio = (endMinutes - startMinutes) / DAY_MINUTES;
              const slotWidth = widthRatio * canvasWidth;
              const isSelected = slot.key === selectedKey;
              const isDragging = drag?.key === slot.key;

              // Until the canvas has been measured, assume there is room rather than
              // flashing an empty bar.
              const showIcon = canvasWidth === 0 || slotWidth >= ICON_ONLY_MIN_WIDTH;
              const showTime = canvasWidth === 0 || slotWidth >= WITH_TIME_MIN_WIDTH;
              const isIconOnly = showIcon && !showTime;

              return (
                <Box
                  key={slot.key}
                  position="absolute"
                  left={`${(startMinutes / DAY_MINUTES) * 100}%`}
                  width={`${widthRatio * 100}%`}
                  top={`${
                    (clamp(block.priority, 1, MAX_PRIORITY_LANES) - 1) * LANE_PITCH + CANVAS_INSET
                  }px`}
                  height={`${LANE_HEIGHT}px`}
                >
                  {/* The live range replaces the slot's own label, which a narrow slot has dropped. */}
                  {isDragging && drag?.mode !== 'move' && (
                    <Flex
                      position="absolute"
                      bottom="100%"
                      right="0"
                      marginBottom="1"
                      paddingX="3"
                      paddingY="1"
                      background="white"
                      borderWidth={1}
                      borderStyle="solid"
                      borderColor="gray.200"
                      borderRadius={4}
                      boxShadow="elevated"
                      whiteSpace="nowrap"
                      zIndex={MAX_PRIORITY_LANES + 2}
                    >
                      {displayTimeRange(block)}
                    </Flex>
                  )}
                  <Flex
                    alignItems="center"
                    justifyContent={isIconOnly ? 'center' : 'flex-start'}
                    gap={`${BLOCK_GAP}px`}
                    height="100%"
                    overflow="hidden"
                    borderRadius={4}
                    background={meta.background}
                    color={TIME_BLOCK_CONTENT_COLOR}
                    padding={
                      isIconOnly ? 0 : `0 ${BLOCK_PADDING_RIGHT}px 0 ${BLOCK_PADDING_LEFT}px`
                    }
                    cursor={isDisabled ? 'not-allowed' : 'ns-resize'}
                    // Only the selected slot is at full strength, so the one being
                    // worked on reads clearly against the stack behind it.
                    opacity={!selectedKey || isSelected ? 1 : 0.5}
                    boxShadow={
                      isSelected ? `inset 0px 0px 0px 2px ${theme.colors.blue[500]}` : undefined
                    }
                    onPointerDown={(event) => beginDrag(event, slot, 'move')}
                  >
                    {showIcon && (
                      <BlockIcon
                        width={`${BLOCK_ICON_SIZE}px`}
                        height={`${BLOCK_ICON_SIZE}px`}
                        flexShrink={0}
                        color={meta.blockIconColor}
                      />
                    )}
                    {showTime && (
                      <chakra.span overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                        {displayTimeRange(block)}
                      </chakra.span>
                    )}
                  </Flex>

                  {isSelected && !isDisabled && (
                    <>
                      <Box
                        position="absolute"
                        top={0}
                        left={0}
                        height="100%"
                        width={`${RESIZE_HANDLE_WIDTH}px`}
                        cursor="ew-resize"
                        onPointerDown={(event) => beginDrag(event, slot, 'resize-start')}
                      />
                      <Box
                        position="absolute"
                        top={0}
                        right={0}
                        height="100%"
                        width={`${RESIZE_HANDLE_WIDTH}px`}
                        cursor="ew-resize"
                        onPointerDown={(event) => beginDrag(event, slot, 'resize-end')}
                      />
                    </>
                  )}
                </Box>
              );
            })}
          </Box>
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button variant="ghost" colorScheme="blue" onClick={onCancel} isDisabled={isSaving}>
          {t('cancel')}
        </Button>
        <Button
          variant="solid"
          colorScheme="blue"
          marginLeft="3"
          onClick={handleSave}
          isDisabled={isDisabled || isSaving || hasIncompleteSlot || !!draftError}
          isLoading={isSaving}
        >
          {t('save')}
        </Button>
      </ModalFooter>
    </ModalContent>
  );
}

function SlotDataField({
  block,
  playlists,
  inputSources,
  apps,
  isDisabled,
  onChange,
}: {
  block: ScheduleTimeBlockInput;
  playlists: PlaylistOption[];
  inputSources: string[];
  apps: AppOption[];
  isDisabled?: boolean;
  onChange: (data: ScheduleTimeBlockInput['data']) => void;
}) {
  const placeholder = t(TIME_BLOCK_DATA_LABEL[block.type]);

  switch (block.type) {
    case TimeBlockType.Playlist:
      return (
        <IconSelect
          value={block.data.id ?? undefined}
          options={playlists.map((playlist) => ({ value: playlist.id, label: playlist.title }))}
          onChange={(id) =>
            onChange({ id, mediaType: playlists.find((p) => p.id === id)?.mediaType })
          }
          placeholder={placeholder}
          isDisabled={isDisabled}
          isSearchable
        />
      );
    case TimeBlockType.Apps:
      return (
        <IconSelect
          value={block.data.applicationId ?? undefined}
          options={apps.map((app) => ({ value: app.applicationId, label: app.name }))}
          onChange={(applicationId) => onChange({ applicationId })}
          placeholder={placeholder}
          isDisabled={isDisabled}
          isSearchable
        />
      );
    case TimeBlockType.DefaultInputSource:
      return null;
    case TimeBlockType.Source:
      return (
        <IconSelect
          value={block.data.source ?? undefined}
          options={inputSources.map((source) => ({ value: source, label: source }))}
          onChange={(source) => onChange({ source })}
          placeholder={placeholder}
          isDisabled={isDisabled}
          isSearchable
        />
      );
    case TimeBlockType.Bookmark:
      return (
        <Input
          background="white"
          placeholder="https://webpage.com"
          value={block.data.url ?? ''}
          isDisabled={isDisabled}
          onChange={(event) => onChange({ url: event.target.value })}
        />
      );
    case TimeBlockType.Brightness:
      return (
        <Slider
          value={block.data.value ?? DEFAULT_BRIGHTNESS}
          onChange={(value) => onChange({ value })}
          leftIcon={BrightnessIcon}
          min={0}
          max={100}
          isDisabled={isDisabled}
        />
      );
    default:
      return null;
  }
}
